import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiMail,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiBell,
  FiTrendingUp,
  FiFileText,
  FiGift,
} from "react-icons/fi";
import { SEO } from "@/components/seo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type SubscriberInfo = {
  email: string;
  status: string;
  preferences: {
    newsletter: boolean;
    rateAlerts: boolean;
    marketUpdates: boolean;
    promotions: boolean;
  };
};

const PREF_OPTIONS = [
  {
    key: "newsletter" as const,
    label: "Newsletter",
    description: "Monthly updates with tips, guides, and industry insights",
    icon: FiFileText,
  },
  {
    key: "rateAlerts" as const,
    label: "Rate Alerts",
    description: "Instant notifications when the RBA changes interest rates",
    icon: FiBell,
  },
  {
    key: "marketUpdates" as const,
    label: "Market Updates",
    description: "Weekly property market trends and analysis",
    icon: FiTrendingUp,
  },
  {
    key: "promotions" as const,
    label: "Special Offers",
    description: "Exclusive deals and promotional offers",
    icon: FiGift,
  },
];

export function PreferencesPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [subscriber, setSubscriber] = useState<SubscriberInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preferences, setPreferences] = useState({
    newsletter: true,
    rateAlerts: true,
    marketUpdates: true,
    promotions: false,
  });
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Missing token. Please use the link from your email.");
      setIsLoading(false);
      return;
    }

    fetch(`/api/unsubscribe/${token}`)
      .then((res) => {
        if (!res.ok) throw new Error("Invalid or expired link");
        return res.json();
      })
      .then((data) => {
        const info = data as SubscriberInfo;
        setSubscriber(info);
        setPreferences(info.preferences);
      })
      .catch((err) => {
        setError(err.message || "Failed to verify link");
      })
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleSave = async () => {
    if (!token) return;

    setIsSaving(true);
    try {
      const res = await fetch("/api/subscribe/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, preferences }),
      });

      const data = (await res.json()) as { message?: string; error?: string };

      if (!res.ok) {
        throw new Error(data.error || "Failed to save preferences");
      }

      setIsSaved(true);
      toast.success("Preferences saved successfully");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleAll = (enabled: boolean) => {
    setPreferences({
      newsletter: enabled,
      rateAlerts: enabled,
      marketUpdates: enabled,
      promotions: enabled,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <FiLoader className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SEO title="Email Preferences" url="/preferences" />
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiAlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-xl font-semibold mb-2">Invalid Link</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <Button asChild>
                <Link to="/">Return Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  if (isSaved) {
    return (
      <>
        <SEO title="Preferences Saved" url="/preferences" />
        <div className="min-h-[60vh] flex items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCheck className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-xl font-semibold mb-2">Preferences Updated</h1>
              <p className="text-muted-foreground mb-6">
                Your email preferences have been saved successfully.
              </p>
              <Button asChild variant="outline">
                <Link to="/">Return Home</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const allEnabled = Object.values(preferences).every(Boolean);
  const allDisabled = Object.values(preferences).every((v) => !v);

  return (
    <>
      <SEO
        title="Email Preferences"
        description="Manage your email subscription preferences"
        url="/preferences"
      />

      <section className="bg-primary text-primary-foreground py-12">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiMail className="w-8 h-8" />
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-medium mb-2">
            Email Preferences
          </h1>
          <p className="text-primary-foreground/80">
            Managing preferences for <strong>{subscriber?.email}</strong>
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Choose Your Emails</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAll(true)}
                    disabled={allEnabled}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleAll(false)}
                    disabled={allDisabled}
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {PREF_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isChecked = preferences[opt.key];

                return (
                  <label
                    key={opt.key}
                    className={`flex items-start gap-4 p-4 border rounded-xl cursor-pointer transition-all ${
                      isChecked
                        ? "border-primary bg-primary/5"
                        : "border-border hover:border-muted-foreground/30"
                    }`}
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        isChecked ? "bg-primary/10" : "bg-muted"
                      }`}
                    >
                      <Icon
                        className={`w-5 h-5 ${
                          isChecked ? "text-primary" : "text-muted-foreground"
                        }`}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{opt.label}</p>
                      <p className="text-sm text-muted-foreground">
                        {opt.description}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          [opt.key]: e.target.checked,
                        })
                      }
                      className="mt-1 h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </label>
                );
              })}

              {allDisabled && (
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                  Note: Disabling all preferences will unsubscribe you from our mailing list.
                </p>
              )}

              <div className="pt-4 flex flex-col sm:flex-row gap-3">
                <Button
                  className="flex-1"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <FiLoader className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FiCheck className="w-4 h-4 mr-2" />
                  )}
                  Save Preferences
                </Button>
                <Button variant="outline" asChild>
                  <Link to={`/unsubscribe?token=${token}`}>
                    Unsubscribe Instead
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </>
  );
}
